<template>
  <div>
    <Header :title="breadcrumb"></Header>
    <v-row>
      <v-col cols="12" sm="12" md="3" lg="2" xl="3">
        <div class="patient-sidebar d-print-none">
          <!-- Patient Mini Profile -->
          <div class="sidebar-profile mb-4" v-if="patient && !isPatientDetailsTab">
            <div class="profile-content">
              <div class="profile-avatar-ring mx-auto mb-3">
                <v-avatar :size="56" color="rgba(255,255,255,0.15)">
                  <span class="white--text text-h5 font-weight-bold">{{ patientInitials }}</span>
                </v-avatar>
              </div>
              <div class="profile-name-row">
                <span class="profile-name">{{ patientName }}</span>
                <v-chip v-if="patient?.sex" x-small dark outlined class="ml-1" style="border-color: rgba(255,255,255,0.4);">
                  <v-icon x-small class="mr-1">{{ patient.sex === 'MALE' ? 'mdi-gender-male' : 'mdi-gender-female' }}</v-icon>
                  {{ patient.sex }}
                </v-chip>
              </div>
              <div class="profile-pills mt-2">
                <span class="profile-pill" v-if="patient?.contact?.contactPhones?.[0]?.phone">
                  <v-icon x-small class="mr-1" style="color: rgba(255,255,255,0.7);">mdi-phone</v-icon>
                  {{ patient.contact.contactPhones[0].phone }}
                </span>
                <span class="profile-pill" v-if="patient?.contact?.email">
                  <v-icon x-small class="mr-1" style="color: rgba(255,255,255,0.7);">mdi-email-outline</v-icon>
                  {{ patient.contact.email }}
                </span>
                <span class="profile-pill" v-if="patient?.referenceNumber">
                  <v-icon x-small class="mr-1" style="color: rgba(255,255,255,0.7);">mdi-identifier</v-icon>
                  {{ patient.referenceNumber }}
                </span>
              </div>
            </div>
          </div>

          <!-- Navigation -->
          <nav class="sidebar-nav">
            <template v-for="(group, gIndex) in navGroups">
              <div v-if="group.items.some(item => $canAny([item.permission]))" :key="group.label" class="nav-group">
                <div v-if="gIndex > 0" class="nav-group-divider"></div>
                <div class="nav-group-label">{{ group.label }}</div>
                <template v-for="item in group.items">
                  <router-link
                    v-if="$canAny([item.permission])"
                    :key="item.title"
                    :to="item.route"
                    class="nav-item"
                    :class="{ 'nav-item--active': isActiveRoute(item.route) }"
                    @click.native="changeBreadcrumb(item.title)"
                  >
                    <v-icon :size="18" class="nav-item-icon" :color="isActiveRoute(item.route) ? 'primary' : ''">{{ item.icon }}</v-icon>
                    <span class="nav-item-text">{{ item.title }}</span>
                    <v-chip
                      v-if="item.total > 0"
                      x-small
                      :color="isActiveRoute(item.route) ? 'primary' : 'grey lighten-2'"
                      :text-color="isActiveRoute(item.route) ? 'white' : 'grey darken-1'"
                      class="nav-item-badge ml-auto"
                    >
                      {{ item.total }}
                    </v-chip>
                  </router-link>
                </template>
              </div>
            </template>
          </nav>
        </div>
      </v-col>
      <v-col>
        <router-view />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Header from "@/components/Header";
import apolloClient from "@/apollo/apollo";
import gql from "graphql-tag";

export default {
  props: ["id"],
  components: { Header },
  data() {
    return {
      patient: null,
      breadcrumb: "",
      patientName: "",
      navGroups: [
        {
          label: "Overview",
          items: [
            {
              title: "Patient Details",
              route: `/patients/${this.$route.params.id}`,
              permission: "patient_details",
              icon: "mdi-account-outline",
            },
            {
              title: "Appointments",
              route: `/patients/${this.$route.params.id}/appointments`,
              permission: "patient_appointment",
              icon: "mdi-calendar-clock",
            },
          ],
        },
        {
          label: "Clinical",
          items: [
            {
              title: "Forms",
              route: `/patients/${this.$route.params.id}/form-signatures`,
              permission: "patient_form",
              icon: "mdi-file-document-outline",
            },
            {
              title: "Department Note",
              route: `/patients/${this.$route.params.id}/departments`,
              permission: "patient_department_note",
              icon: "mdi-clipboard-text-outline",
            },
            {
              title: "Treatment Note",
              route: `/patients/${this.$route.params.id}/treatment-notes`,
              permission: "patient_treatment_note",
              icon: "mdi-stethoscope",
            },
            {
              title: "Public Treatment Note",
              route: `/patients/${this.$route.params.id}/public-treatment-notes`,
              permission: "authenticated",
              icon: "mdi-file-eye-outline",
            },
            {
              title: "Archive Treatment Note",
              route: `/patients/${this.$route.params.id}/archived-treatment-notes`,
              permission: "patient_archive_treatment_note",
              icon: "mdi-archive-outline",
            },
          ],
        },
        {
          label: "Programs & Files",
          items: [
            {
              title: "Packages",
              route: `/patients/${this.$route.params.id}/packages`,
              permission: "*",
              icon: "mdi-package-variant",
            },
            {
              title: "Files",
              route: `/patients/${this.$route.params.id}/files`,
              permission: "patient_files",
              icon: "mdi-folder-outline",
            },
            {
              title: "Child Progress & Media",
              route: `/patients/${this.$route.params.id}/media`,
              permission: "patient_media",
              icon: "mdi-image-multiple",
            },
          ],
        },
        {
          label: "Administrative",
          items: [
            {
              title: "Reports",
              route: `/patients/${this.$route.params.id}/reports`,
              permission: "authenticated",
              icon: "mdi-chart-bar",
            },
            {
              title: "Policies",
              route: `/patients/${this.$route.params.id}/policies`,
              permission: "patient_policy_status_view",
              icon: "mdi-shield-check-outline",
            },
          ],
        },
      ],
    };
  },
  computed: {
    patientInitials() {
      if (!this.patient?.contact) return '?';
      const f = this.patient.contact.firstName || '';
      const l = this.patient.contact.lastName || '';
      return ((f[0] || '') + (l[0] || '')).toUpperCase() || '?';
    },
    isPatientDetailsTab() {
      const p = this.$route.path;
      const id = this.$route.params.id;
      return p === `/patients/${id}` || p === `/patients/${id}/`;
    },
  },
  methods: {
    isActiveRoute(route) {
      return this.$route.path === route;
    },
    changeBreadcrumb(title) {
      this.updateHeaderTitle();
    },
    computeSectionFromRoute(route) {
      const p = route.path;
      const id = route.params.id;

      if (p === `/patients/${id}` || p === `/patients/${id}/`) {
        return "Patient Details";
      }
      if (p.includes("/appointments")) return "Appointments";
      if (p.includes("/form-signatures")) return "Forms";
      if (p.includes("/departments")) return "Department Notes";
      if (p.includes("/public-treatment-notes")) return "Public Treatment Notes";
      if (p.includes("/treatment-notes")) return "Treatment Notes";
      if (p.includes("/packages")) return "Packages";
      if (p.includes("/files/private")) return "Private Files";
      if (p.includes("/files")) return "Files";
      if (p.includes("/media")) return "Media";
      if (p.includes("/archived-treatment-notes")) return "Archive Treatment Note";
      if (p.includes("/reports")) return "Reports";
      if (p.includes("/policies")) return "Policies";

      return route.meta?.breadcrumb || "Patient Details";
    },
    updateHeaderTitle() {
      const section = this.computeSectionFromRoute(this.$route);
      const name = this.patientName?.trim();
      document.title = name;
      this.breadcrumb = name ? `${name} — ${section}` : section;
    },
  },

  async mounted() {
    this.updateHeaderTitle();

    try {
      const response = await apolloClient.query({
        query: gql`
          query findOnePatientById($id: Int!) {
            findOnePatientById(id: $id) {
              submenuStatics {
                files
                appointments
                forms
                treatmentNotes
                media
                publicTreatmentNotes
              }
              id
              listOfFormSignId
              nationalId
              appointmentNotes
              contact {
                country {
                  id
                }
                firstName
                lastName
                state
                address1
                address2
                address3
                city
                companyName
                contactPhones {
                  id
                  phone
                  type
                }
                email
                id
                note
                occupation
                postCode
                preferredName
                timeZone
                type
              }
              extraInfo
              sex
              birthDate
              bookingEmailConfirm
              cancelEmailConfirm
              acceptPolicy
              emergencyContact
              referenceNumber
              medicareNumber
            }
          }
        `,
        variables: { id: +this.$route.params.id },
        context: {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
        },
      });

      this.patient = response.data.findOnePatientById;

      // Update submenu item counts across all groups
      const statics = response.data.findOnePatientById.submenuStatics;
      this.navGroups.forEach((group) => {
        group.items.forEach((item) => {
          if (item.title === "Appointments") item.total = statics.appointments;
          else if (item.title === "Forms") item.total = statics.forms;
          else if (item.title === "Files") item.total = statics.files;
          else if (item.title === "Treatment Note") item.total = statics.treatmentNotes;
          else if (item.title === "Child Progress & Media") item.total = statics.media;
          else if (item.title === "Public Treatment Note") item.total = statics.publicTreatmentNotes;
        });
      });

      this.patientName =
        response.data.findOnePatientById.contact.firstName +
        " " +
        response.data.findOnePatientById.contact.lastName;

      this.updateHeaderTitle();
    } catch (error) {
      console.error("Failed to fetch:", error.message);
    }
  },

  watch: {
    $route() {
      this.updateHeaderTitle();
    },
  },
};
</script>

<style scoped>
/* Sidebar Container */
.patient-sidebar {
  position: sticky;
  top: 80px;
}

/* Profile Card */
.sidebar-profile {
  background: linear-gradient(135deg, #14AAA6 0%, #0d7a77 50%, #0a6663 100%);
  border-radius: 16px;
  padding: 20px 16px;
  box-shadow: 0 8px 32px rgba(20, 170, 166, 0.25);
  position: relative;
  overflow: hidden;
}
.sidebar-profile::before {
  content: '';
  position: absolute;
  top: -40%;
  right: -30%;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  pointer-events: none;
}
.profile-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.profile-avatar-ring {
  padding: 3px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(255,255,255,0.5), rgba(255,255,255,0.15));
  box-shadow: 0 0 20px rgba(255,255,255,0.15);
}
.profile-name-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}
.profile-name {
  color: #fff;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.3;
  letter-spacing: -0.2px;
}
.profile-pills {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
}
.profile-pill {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.95);
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-weight: 500;
  transition: all 0.2s ease;
}
.profile-pill:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Navigation */
.sidebar-nav {
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  padding: 8px 0;
}

/* Group */
.nav-group {
  padding: 0;
}
.nav-group-divider {
  height: 1px;
  background-color: rgba(0, 0, 0, 0.06);
  margin: 6px 16px;
}
.nav-group-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #b0b0b0;
  padding: 10px 16px 6px 16px;
}

/* Nav Item */
.nav-item {
  display: flex;
  align-items: center;
  padding: 9px 16px;
  margin: 1px 8px;
  border-radius: 8px;
  text-decoration: none;
  color: #616161;
  font-size: 0.82rem;
  font-weight: 500;
  transition: all 0.15s ease;
  position: relative;
  cursor: pointer;
}
.nav-item:hover {
  background-color: #f5f7fa;
  color: #212121;
}
.nav-item-icon {
  margin-right: 10px;
  color: #9e9e9e !important;
  transition: color 0.15s ease;
}
.nav-item:hover .nav-item-icon {
  color: #616161 !important;
}
.nav-item-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.nav-item-badge {
  font-size: 0.65rem !important;
  height: 18px !important;
  min-width: 18px !important;
}

/* Active State */
.nav-item--active {
  background-color: rgba(20, 170, 166, 0.08) !important;
  color: #0d7a77 !important;
  font-weight: 600;
}
.nav-item--active .nav-item-icon {
  color: #14AAA6 !important;
}
.nav-item--active::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  background-color: #14AAA6;
  border-radius: 0 3px 3px 0;
}

/* Print */
@media print {
  .d-print-none {
    display: none !important;
  }
}

/* Responsive: collapse sidebar profile on very small screens */
@media (max-width: 959px) {
  .patient-sidebar {
    position: static;
  }
  .profile-content {
    flex-direction: row;
    text-align: left;
    gap: 12px;
  }
  .profile-avatar-ring {
    margin: 0 !important;
  }
  .profile-pills {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
}
</style>
